/**
 * Modal d'édition produit (admin staff). Permet de saisir l'overlay
 * d'enrichissement par-dessus la donnée Cashpad : photo, description, allergènes,
 * badges. Photo upload via dataURL base64 → POST /pos/products-enriched/:id/photo
 * (pattern wine-photos). Le reste via PUT /pos/products-enriched/:id.
 *
 * UX : si une nouvelle photo est sélectionnée, le POST photo est fait d'abord
 * (il met à jour photo_url côté backend), puis le PUT pour les autres champs.
 */
import React, { useEffect, useRef, useState } from 'react';
import './EditProductSheet.css';
import { apiFetch } from '../../utils/api';

const ALLERGENES = [
  'gluten', 'lactose', 'oeuf', 'soja', 'fruits à coque', 'arachide',
  'poisson', 'crustacés', 'mollusques', 'sésame', 'céleri', 'moutarde',
  'sulfites', 'lupin',
];
const BADGES = [
  'vegan', 'végétarien', 'sans gluten', 'sans alcool', 'bio', 'fait maison', 'local',
  'de saison', 'pour partager', 'à emporter', 'signature', 'coup de cœur du chef', 'nouveau',
];
const DESCRIPTION_MAX = 500;

export interface ProductEnriched {
  cashpadId: string;
  photoUrl: string | null;
  description: string | null;
  allergenes: string[];
  badges: string[];
  hiddenInClient: boolean;
  updatedAt: number;
  updatedBy: string | null;
}

export interface EditableProduct {
  id: string;
  name: string;
  enriched?: ProductEnriched | null;
}

interface Props {
  product: EditableProduct | null;
  onClose: () => void;
  onSaved: (updated: ProductEnriched) => void;
}

// Downscale + ré-encode JPEG avant base64 : iPhone 17 Pro produit des JPEG
// de 25-30 MB qui en base64 font 35-40 MB → risque OOM WKWebView au moment
// du POST. Le backend recompresse en 600×600 de toute façon, on n'a pas besoin
// d'envoyer la pleine résolution. 1600px max + qualité 0.85 ≈ 200-400 KB.
async function fileToDataUrl(file: File, maxEdge = 1600, quality = 0.85): Promise<string> {
  // Voie principale : createImageBitmap avec resizeWidth/Height → décode
  // DIRECTEMENT à la taille réduite, sans jamais tenir le bitmap plein format en
  // mémoire (une photo 48 MP = ~195 MB décodés = crash WKWebView). Fallback
  // `new Image()` pour très vieux iOS sans createImageBitmap.
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const resized = await createImageBitmap(bitmap, {
      resizeWidth: w,
      resizeHeight: h,
      resizeQuality: 'high',
    });
    bitmap.close();
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context unavailable');
    ctx.drawImage(resized, 0, 0);
    resized.close();
    return canvas.toDataURL('image/jpeg', quality);
  } catch {
    const objectUrl = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Image load failed'));
        el.src = objectUrl;
      });
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D context unavailable');
      ctx.drawImage(img, 0, 0, w, h);
      return canvas.toDataURL('image/jpeg', quality);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

export function EditProductSheet({ product, onClose, onSaved }: Props) {
  const [description, setDescription] = useState('');
  const [allergenes, setAllergenes] = useState<string[]>([]);
  const [badges, setBadges] = useState<string[]>([]);
  const [hiddenInClient, setHiddenInClient] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pendingPhotoDataUrl, setPendingPhotoDataUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Hydrate state quand le produit change.
  useEffect(() => {
    if (!product) return;
    const e = product.enriched ?? null;
    setDescription(e?.description ?? '');
    setAllergenes(e?.allergenes ?? []);
    setBadges(e?.badges ?? []);
    setHiddenInClient(e?.hiddenInClient ?? false);
    setPreviewUrl(e?.photoUrl ?? null);
    setPendingPhotoDataUrl(null);
    setToast(null);
  }, [product?.id]);

  // Masque la TabBar tant que la modale est ouverte — même class que
  // ProductDetailSheet (cf TabBar.css).
  useEffect(() => {
    if (!product) return;
    document.body.classList.add('modal-sheet-open');
    return () => document.body.classList.remove('modal-sheet-open');
  }, [product]);

  if (!product) return null;

  const toggleChip = (list: string[], setter: (v: string[]) => void, value: string) => {
    setter(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  };

  // Pipeline unique : validation taille/type → canvas downscale → base64 →
  // preview locale. Partagé entre file input picker et drag&drop.
  const handleFile = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      setToast({ kind: 'err', msg: 'Photo trop lourde (>10 MB)' });
      return;
    }
    if (!file.type.startsWith('image/')) {
      setToast({ kind: 'err', msg: 'Format invalide (image attendue)' });
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setPendingPhotoDataUrl(dataUrl);
      setPreviewUrl(dataUrl);
    } catch {
      setToast({ kind: 'err', msg: 'Lecture du fichier échouée' });
    }
  };

  const onPickPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await handleFile(file);
  };

  const onDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      setIsDraggingOver(true);
    }
  };
  const onDragLeave = () => setIsDraggingOver(false);
  const onDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const onSave = async () => {
    if (saving) return;
    setSaving(true);
    setToast(null);
    const token = localStorage.getItem('alterrego_jwt');
    if (!token) {
      setToast({ kind: 'err', msg: 'Non connecté' });
      setSaving(false);
      return;
    }
    const headers = { 'Content-Type': 'application/json' };
    try {
      let nextPhotoUrl: string | null | undefined; // undefined = champ non touché
      if (pendingPhotoDataUrl) {
        const r = await apiFetch(
          `/pos/products-enriched/${encodeURIComponent(product.id)}/photo`,
          { method: 'POST', headers, body: JSON.stringify({ dataUrl: pendingPhotoDataUrl }) },
        );
        if (!r.ok) {
          const txt = await r.text().catch(() => '');
          throw new Error(`Photo upload ${r.status}: ${txt.slice(0, 120)}`);
        }
        const data = await r.json();
        nextPhotoUrl = data.photoUrl ?? null;
      }

      // PUT pour description / allergènes / badges. On omet photoUrl sauf si on
      // vient juste de le poser (préserve la sémantique upsert undefined = noop).
      const putBody: Record<string, unknown> = {
        description: description.trim() || null,
        allergenes,
        badges,
        hiddenInClient,
      };
      if (nextPhotoUrl !== undefined) putBody.photoUrl = nextPhotoUrl;

      const r2 = await apiFetch(
        `/pos/products-enriched/${encodeURIComponent(product.id)}`,
        { method: 'PUT', headers, body: JSON.stringify(putBody) },
      );
      if (!r2.ok) {
        const txt = await r2.text().catch(() => '');
        throw new Error(`Save ${r2.status}: ${txt.slice(0, 120)}`);
      }
      const updated: ProductEnriched = await r2.json();
      setToast({ kind: 'ok', msg: 'Enregistré' });
      onSaved(updated);
      // Petite latence pour laisser le toast visible.
      setTimeout(() => onClose(), 600);
    } catch (err: any) {
      setToast({ kind: 'err', msg: err?.message ?? 'Erreur réseau' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="eps-backdrop" onClick={onClose}>
      <div className="eps-sheet" onClick={(e) => e.stopPropagation()}>
        <button className="eps-close" onClick={onClose} aria-label="Fermer">×</button>

        <div className="eps-header">
          <h2>Éditer la fiche</h2>
          <p className="eps-product-name">{product.name}</p>
        </div>

        {/* Bloc haut 2 colonnes : photo carrée (gauche) + tags (droite). */}
        <div className="eps-top-grid">
        {/* Photo — tap zone (caméra/photothèque/fichier) + drag&drop desktop */}
        <div className="eps-section eps-photo-col">
          <h3>Photo</h3>
          <button
            type="button"
            className={`eps-photo-tap${previewUrl ? ' has-photo' : ''}${isDraggingOver ? ' dragging-over' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            aria-label={previewUrl ? 'Changer la photo' : 'Ajouter une photo'}
          >
            {previewUrl ? (
              <>
                <img src={previewUrl} alt="" />
                <span className="eps-photo-edit-badge" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>
                </span>
              </>
            ) : (
              <span className="eps-photo-empty-content">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <span>Touchez pour ajouter une photo</span>
                <span className="eps-photo-empty-drop-hint">ou glissez un fichier</span>
              </span>
            )}
          </button>
          {/*
            Pas de `capture` attribute : iOS affiche alors le sheet natif
            "Prendre une photo / Photothèque / Choisir un fichier". Avec
            capture="environment" on forçait la caméra (limitant).
          */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={onPickPhoto}
          />
          <p className="eps-hint">Carrée, &lt; 10 MB</p>
        </div>

          {/* Tags (= badges) — colonne droite */}
          <div className="eps-section eps-tags-col">
            <h3>Tags</h3>
            <div className="eps-chips">
              {BADGES.map((b) => (
                <button
                  key={b}
                  type="button"
                  className={`eps-chip${b === 'signature' ? ' eps-chip-signature' : ''} ${badges.includes(b) ? 'active' : ''}`}
                  onClick={() => toggleChip(badges, setBadges, b)}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="eps-section">
          <h3>Description</h3>
          <textarea
            className="eps-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
            placeholder="Suggestions du chef, accord vin, anecdote…"
            rows={4}
          />
          <p className="eps-counter">{description.length} / {DESCRIPTION_MAX}</p>
        </div>

        {/* Allergènes */}
        <div className="eps-section">
          <h3>Allergènes</h3>
          <div className="eps-chips">
            {ALLERGENES.map((a) => (
              <button
                key={a}
                type="button"
                className={`eps-chip ${allergenes.includes(a) ? 'active' : ''}`}
                onClick={() => toggleChip(allergenes, setAllergenes, a)}
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        {/* Masquage côté client */}
        <div className="eps-section">
          <button
            type="button"
            className={`eps-toggle-row ${hiddenInClient ? 'on' : ''}`}
            role="switch"
            aria-checked={hiddenInClient}
            onClick={() => setHiddenInClient((v) => !v)}
          >
            <span className="eps-toggle-text">
              <strong>Masquer côté client</strong>
              <span>Le produit reste actif sur la caisse mais n'apparaît pas dans la carte côté client.</span>
            </span>
            <span className="eps-switch" aria-hidden="true"><span className="eps-switch-knob" /></span>
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`eps-toast ${toast.kind}`}>{toast.msg}</div>
        )}

        {/* Actions */}
        <div className="eps-actions">
          <button type="button" className="eps-btn eps-btn-secondary" onClick={onClose} disabled={saving}>
            Annuler
          </button>
          <button type="button" className="eps-btn eps-btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
